import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { identifyUser, resetUser } from "@/lib/analytics";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [family, setFamily] = useState(null);
  const [patient, setPatient] = useState(null);
  const [role, setRole] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me.user);
      setFamily(me.family);
      setPatient(me.patient ?? null);
      setRole(me.role);
      identifyUser(me.user, {
        has_family: !!me.family,
        has_care_recipient: !!me.patient || !!me.family?.loved_one?.name,
        family_role: me.role || undefined,
      });
    } catch {
      setUser(null); setFamily(null); setPatient(null); setRole(null);
    }
  }, []);

  useEffect(() => {
    (async () => { await refresh(); setReady(true); })();
  }, [refresh]);

  const signIn = useCallback(async (email, password) => {
    await api.login(email, password);
    await refresh();
  }, [refresh]);

  const signUp = useCallback(async (email, password, name, attribution) => {
    await api.signup(email, password, name, attribution);
    await refresh();
  }, [refresh]);

  const signInWithGoogle = useCallback(async () => {
    // For web: redirect_uri points to a backend route that will redirect back
    // to the frontend with code+state in the URL hash/query.
    const redirect_uri = `${window.location.origin}/api/auth/google/web-callback`;
    const res = await api.googleStart(redirect_uri);
    // Store state in sessionStorage so we can verify it when Google redirects back
    sessionStorage.setItem("google_oauth_state", res.state);
    sessionStorage.setItem("google_oauth_redirect_uri", redirect_uri);
    // Redirect to Google consent screen
    window.location.href = res.authorize_url;
  }, []);

  // Handle Google OAuth callback on page load (when redirected back from Google)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const savedState = sessionStorage.getItem("google_oauth_state");
    const savedRedirectUri = sessionStorage.getItem("google_oauth_redirect_uri");
    if (code && state && savedState === state && savedRedirectUri) {
      sessionStorage.removeItem("google_oauth_state");
      sessionStorage.removeItem("google_oauth_redirect_uri");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      (async () => {
        try {
          await api.googleCallback(code, state, savedRedirectUri);
          await refresh();
        } catch (e) {
          console.warn("Google sign-in callback failed:", e);
        }
      })();
    }
  }, [refresh]);

  const signOut = useCallback(async () => {
    try { await api.logout(); } catch (e) { console.debug("logout best-effort failed; clearing locally", e); }
    setUser(null); setFamily(null); setPatient(null); setRole(null);
    resetUser();
  }, []);

  const value = useMemo(
    () => ({ ready, user, family, patient, role, refresh, signIn, signUp, signOut, signInWithGoogle }),
    [ready, user, family, patient, role, refresh, signIn, signUp, signOut, signInWithGoogle],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("AuthProvider missing");
  return v;
};
